import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Calendar, 
  Clock, 
  PawPrint, 
  Edit, 
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  ChevronUp,
  ChevronDown,
  User,
  Stethoscope,
  Syringe,
  FileText,
  Save,
  LayoutGrid,
  CalendarDays
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getAppointments, addAppointment } from '../services/appointmentService';
import { updateAppointment, deleteAppointment } from '../services/appointmentService';
import { supabase } from '../services/mockData';
import { getPets } from '../services/petService';
import { getOwners } from '../services/ownerService';
import { Appointment, Pet, Owner } from '../types';
import { useNewAppointments } from '../hooks/useNewAppointments';
import { SkeletonListPage } from '../components/Skeleton/Skeleton';
import EmptyState from '../components/EmptyState/EmptyState';
import AppointmentCalendar from '../components/Calendar/AppointmentCalendar';
import { todayLocal, addDaysLocal, normalizeToLocalDate } from '../utils/dateUtils';
import DeleteConfirmModal from '../components/Modals/DeleteConfirmModal';
import { normalizeTr } from '../utils/normalizeTr';

const Appointments: React.FC = () => {
  const { markAllRead } = useNewAppointments();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [owners, setOwners] = useState<Owner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState<Appointment | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'calendar'>('cards');
  const [sortField, setSortField] = useState<'date' | 'time' | 'petName' | 'ownerName' | 'type' | 'status'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [formData, setFormData] = useState({
    pet_id: '',
    date: todayLocal(),
    time: '',
    type: 'checkup' as 'checkup' | 'vaccination' | 'surgery' | 'emergency' | 'examination' | 'other',
    notes: ''
  });
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);

  const normalizeDate = (value: string) => {
    try {
      return normalizeToLocalDate(value);
    } catch {
      return value;
    }
  };

  const fetchAppointments = () => {
    setLoading(true);
    getAppointments()
      .then(data => setAppointments(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAppointments();
    getPets().then(setPets).catch(err => toast.error('Hasta listesi yüklenemedi: ' + (err?.message || 'bilinmeyen hata')));
    getOwners().then(setOwners).catch(err => toast.error('Sahip listesi yüklenemedi: ' + (err?.message || 'bilinmeyen hata')));
    // Sayfa açıldığında "yeni" sayacını sıfırla
    markAllRead();

    // Realtime: yeni randevu gelirse listeyi otomatik güncelle
    const channelName = `appointments-page-${Date.now()}-${Math.random()}`;
    const channel = supabase.channel(channelName);
    channel
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'appointments' },
        () => fetchAppointments()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAppointmentWithPet = (appointment: Appointment) => {
    const pet = pets.find(p => p.id === appointment.pet_id || p.id === appointment.petId);
    const owner = pet ? owners.find(o => o.id === pet.owner_id) : null;
    return { ...appointment, pet, owner };
  };

  const handleSort = (field: 'date' | 'time' | 'petName' | 'ownerName' | 'type' | 'status') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: 'date' | 'time' | 'petName' | 'ownerName' | 'type' | 'status') => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  const filteredAppointments = appointments
    .map(getAppointmentWithPet)
    .filter(appointment => {
      const matchesSearch = 
        normalizeTr(appointment.pet?.name).includes(normalizeTr(searchTerm)) ||
        normalizeTr(appointment.pet?.owner?.name).includes(normalizeTr(searchTerm)) ||
        normalizeTr(appointment.notes).includes(normalizeTr(searchTerm));
      const matchesDate = !selectedDate || normalizeDate(appointment.date) === selectedDate;
      return matchesSearch && matchesDate;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case 'time':
          aValue = a.time || '';
          bValue = b.time || '';
          break;
        case 'petName':
          aValue = a.pet?.name || '';
          bValue = b.pet?.name || '';
          break;
        case 'ownerName':
          aValue = a.pet?.owner?.name || '';
          bValue = b.pet?.owner?.name || '';
          break;
        case 'type':
          aValue = a.type || '';
          bValue = b.type || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          return 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'no-show':
        return <AlertCircle className="w-4 h-4 text-orange-600" />;
      default:
        return <Clock className="w-4 h-4 text-blue-600" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'Planlandı';
      case 'completed':
        return 'Tamamlandı';
      case 'cancelled':
        return 'İptal';
      case 'no-show':
        return 'Gelmedi';
      default:
        return status;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'checkup':
        return 'Kontrol';
      case 'vaccination':
        return 'Aşı';
      case 'surgery':
        return 'Ameliyat';
      case 'emergency':
        return 'Acil';
      default:
        return 'Diğer';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'checkup':
        return 'bg-blue-100 text-blue-800';
      case 'vaccination':
        return 'bg-green-100 text-green-800';
      case 'surgery':
        return 'bg-purple-100 text-purple-800';
      case 'emergency':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-[#F0EBE1] text-[#2B2B2B]';
    }
  };

  const handleStatusChange = (appointmentId: string, newStatus: string) => {
    updateAppointment(appointmentId, { status: newStatus as any })
      .then(() => {
        setAppointments(prev => prev.map(apt => 
          apt.id === appointmentId ? { ...apt, status: newStatus as any } : apt
        ));
        toast.success('Randevu durumu güncellendi!');
      })
      .catch(err => toast.error('Hata: ' + err.message));
  };

  const handleDelete = async (appointmentId: string) => {
    try {
      await deleteAppointment(appointmentId);
      setAppointments(prev => prev.filter(a => a.id !== appointmentId));
      toast.success('Randevu silindi');
    } catch (err: any) {
      toast.error('Hata: ' + (err?.message || 'Randevu silinemedi'));
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.pet_id || !payload.date || !payload.time) {
        toast.error('Hasta, tarih ve saat zorunludur');
        return;
      }
      const duplicate = appointments.some(a =>
        a.id !== editingAppointment?.id &&
        a.status !== 'cancelled' &&
        (a.pet_id || a.petId) === payload.pet_id &&
        normalizeDate(a.date) === payload.date &&
        (a.time || '').slice(0, 5) === payload.time.slice(0, 5)
      );
      if (duplicate) {
        toast.error('Bu hasta için aynı tarih ve saatte zaten bir randevu var');
        return;
      }
      if (editingAppointment) {
        const updated = await updateAppointment(editingAppointment.id, payload as any);
        toast.success('Randevu güncellendi!');
        setShowForm(false);
        setEditingAppointment(null);
        setAppointments(prev => prev.map(a => a.id === updated.id ? { ...a, ...updated } : a));
        setSelectedDate(normalizeDate(updated.date));
      } else {
        const created = await addAppointment({ ...payload, status: 'scheduled' as const } as any);
        toast.success('Randevu başarıyla eklendi!');
        setShowForm(false);
        // Tarih filtresini yeni kayda ayarla ki listede görünsün
        setSelectedDate(normalizeDate(created.date));
        // Optimistic UI: yeni kaydı listeye ekle
        const pet = pets.find(p => p.id === (created.pet_id || (created as any).petId));
        const owner = pet ? owners.find(o => o.id === pet.owner_id) : undefined;
        setAppointments(prev => [{ ...created, pet: pet ? { ...pet, owner } : undefined } as any, ...prev]);
      }
      setFormData({
        pet_id: '',
        date: todayLocal(),
        time: '',
        type: 'checkup',
        notes: ''
      });
      fetchAppointments();
    } catch (err: any) {
      toast.error('Hata: ' + err.message);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const today = todayLocal();
  const todayAppointments = filteredAppointments.filter(apt => normalizeDate(apt.date) === today);

  if (loading) return <SkeletonListPage />;
  if (error) return <div className="text-center py-12 text-red-600">{error}</div>;

  return (
    <div className="page-stack">
      {/* Page Header */}
      <div className="page-head">
        <div>
          <h1 className="page-title">Randevular</h1>
          <p className="page-subtitle">Tüm randevu kayıtları</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {/* View toggle */}
          <div className="flex bg-white border border-[#E8E2D4] rounded-xl p-0.5">
            <button
              onClick={() => setViewMode('cards')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === 'cards'
                  ? 'bg-[#557236] text-white shadow-sm'
                  : 'text-[#8A8676] hover:bg-[#F7F4ED]'
              }`}
              title="Kart görünümü"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              Kartlar
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === 'calendar'
                  ? 'bg-[#557236] text-white shadow-sm'
                  : 'text-[#8A8676] hover:bg-[#F7F4ED]'
              }`}
              title="Takvim görünümü"
            >
              <CalendarDays className="w-3.5 h-3.5" />
              Takvim
            </button>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4" />
            <span>Yeni</span>
          </button>
        </div>
      </div>

      {/* Filters — sadece kart modunda */}
      {viewMode === 'cards' && (
      <div className="dribbble-panel p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#B0A99A] w-5 h-5" />
            <input
              type="text"
              placeholder="Randevu ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#B0A99A] w-5 h-5" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedDate(today)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedDate === today
                ? 'bg-[#557236] text-white'
                : 'bg-[#F0EBE1] text-[#8A8676] hover:bg-[#E8EFD9] hover:text-[#3F5728]'
            }`}
          >
            Bugün
          </button>
          <button
            onClick={() => {
              setSelectedDate(addDaysLocal(1));
            }}
            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-[#F0EBE1] text-[#8A8676] hover:bg-[#E8EFD9] hover:text-[#3F5728] transition-colors"
          >
            Yarın
          </button>
          <button
            onClick={() => setSelectedDate('')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedDate === ''
                ? 'bg-[#557236] text-white'
                : 'bg-[#F0EBE1] text-[#8A8676] hover:bg-[#E8EFD9] hover:text-[#3F5728]'
            }`}
          >
            Tümü
          </button>
        </div>
      </div>
      )}

      {/* Today's Appointments Alert */}
      {viewMode === 'cards' && todayAppointments.length > 0 && (
        <div className="dribbble-panel border-l-4 border-blue-500 bg-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Calendar className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="font-semibold text-blue-800">Bugünkü Randevular</h3>
                <p className="text-sm text-blue-700">
                  {todayAppointments.length} randevu bugün
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-blue-800">
                {new Date().toLocaleDateString('tr-TR', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
          
          {/* Bugünkü randevuların detaylı listesi */}
          <div className="mt-4 space-y-2">
            {todayAppointments.map((appointment, index) => (
              <motion.div
                key={appointment.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200"
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${getTypeColor(appointment.type)}`}>
                    {getTypeLabel(appointment.type) === 'Kontrol' && <Stethoscope className="w-4 h-4" />}
                    {getTypeLabel(appointment.type) === 'Aşı' && <Syringe className="w-4 h-4" />}
                    {getTypeLabel(appointment.type) === 'Ameliyat' && <FileText className="w-4 h-4" />}
                    {getTypeLabel(appointment.type) === 'Acil' && <AlertCircle className="w-4 h-4" />}
                    {getTypeLabel(appointment.type) === 'Diğer' && <Calendar className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className="font-medium text-[#2B2B2B]">
                      {appointment.time} - {appointment.pet?.name}
                    </p>
                    <p className="text-sm text-[#8A8676]">
                      {appointment.pet?.owner?.name} • {getTypeLabel(appointment.type)}
                    </p>
                    {appointment.notes && (
                      <p className="text-xs text-[#8A8676] mt-1">
                        {appointment.notes}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(appointment.status)}
                  <span className="text-sm text-[#8A8676]">
                    {getStatusLabel(appointment.status)}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Takvim görünümü */}
      {viewMode === 'calendar' && (
        <AppointmentCalendar
          appointments={appointments.map(getAppointmentWithPet)}
          onAppointmentClick={(appt) => {
            setEditingAppointment(appt);
            setFormData({
              pet_id: appt.pet_id || appt.petId || '',
              date: normalizeDate(appt.date),
              time: appt.time || '',
              type: appt.type,
              notes: appt.notes || '',
            });
            setShowForm(true);
          }}
        />
      )}

      {/* Kart görünümü */}
      {viewMode === 'cards' && (filteredAppointments.length === 0 ? (
        <div className="dribbble-panel overflow-hidden">
          <EmptyState
            icon={Calendar}
            iconColor="cyan"
            title={appointments.length === 0 ? "Henüz randevu yok" : "Randevu bulunamadı"}
            description={appointments.length === 0
              ? "İlk randevunuzu oluşturarak takvim yönetimine başlayın."
              : "Seçilen kriterlere uygun randevu bulunmuyor. Filtreleri temizleyip tekrar deneyin."}
            actionLabel={appointments.length === 0 ? "Yeni Randevu Oluştur" : undefined}
            onAction={appointments.length === 0 ? () => setShowForm(true) : undefined}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredAppointments.map((appointment, index) => {
            const typeIconClass =
              appointment.type === 'checkup' ? 'bg-[#E8EFD9] text-[#557236]' :
              appointment.type === 'vaccination' ? 'bg-purple-100 text-purple-600' :
              appointment.type === 'surgery' ? 'bg-red-100 text-red-600' :
              appointment.type === 'emergency' ? 'bg-amber-100 text-amber-600' :
              'bg-[#F0EBE1] text-[#8A8676]';

            return (
              <motion.div
                key={appointment.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="dribbble-panel p-4 group relative"
              >
                {/* Üst — Tarih/Saat + Tür ikonu */}
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-2xl ${typeIconClass} flex items-center justify-center shrink-0`}>
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#2B2B2B] truncate">{formatDate(appointment.date)}</p>
                    <p className="text-xs text-[#8A8676] flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {appointment.time || '—'}
                    </p>
                  </div>
                  {/* Tür badge + mobil rozeti */}
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${getTypeColor(appointment.type)}`}>
                      {getTypeLabel(appointment.type)}
                    </span>
                    {(appointment as any).source === 'mobile' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-[#E8EFD9] text-[#3F5728] text-[9px] font-bold border border-[#D2DEB4]">
                        📱 Mobil
                      </span>
                    )}
                  </div>
                </div>

                {/* Hasta + Sahip rozetleri */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B]">
                    <PawPrint className="w-3 h-3" />
                    {appointment.pet?.name}
                  </span>
                  {appointment.pet?.owner?.name && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B]">
                      <User className="w-3 h-3" />
                      {appointment.pet.owner.name}
                    </span>
                  )}
                </div>

                {/* Status */}
                <div className="flex items-center gap-1.5 mb-3">
                  {getStatusIcon(appointment.status)}
                  <span className="text-xs font-semibold text-[#2B2B2B]">
                    {getStatusLabel(appointment.status)}
                  </span>
                </div>

                {/* Not */}
                {appointment.notes && (
                  <div className="flex items-start gap-1.5" title={appointment.notes}>
                    <FileText className="w-3 h-3 text-[#B0A99A] shrink-0 mt-0.5" />
                    <p className="text-[11px] text-[#8A8676] line-clamp-2 italic flex-1">
                      {appointment.notes}
                    </p>
                  </div>
                )}

                {/* Aksiyon — hover ile sağ üstte */}
                <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 rounded-lg p-1 shadow-sm border border-[#F0EBE1]">
                  <select
                    value={appointment.status}
                    onChange={(e) => { e.stopPropagation(); handleStatusChange(appointment.id, e.target.value); }}
                    className="text-[10px] border border-[#E8E2D4] rounded px-1.5 py-0.5 bg-white"
                  >
                    <option value="scheduled">Planlandı</option>
                    <option value="completed">Tamamlandı</option>
                    <option value="cancelled">İptal</option>
                    <option value="no-show">Gelmedi</option>
                  </select>
                  <button
                    onClick={() => {
                      setEditingAppointment(appointment);
                      setFormData({
                        pet_id: appointment.pet_id || (appointment as any).petId || '',
                        date: normalizeDate(appointment.date),
                        time: appointment.time || '',
                        type: appointment.type,
                        notes: appointment.notes || '',
                      });
                      setShowForm(true);
                    }}
                    className="w-6 h-6 rounded-md hover:bg-blue-50 text-blue-600 flex items-center justify-center"
                    title="Düzenle"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setAppointmentToDelete(appointment)}
                    className="w-6 h-6 rounded-md hover:bg-red-50 text-red-600 flex items-center justify-center"
                    title="Sil"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      ))}

      {/* Appointment Form Modal */}
      {showForm && createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4" style={{backgroundColor:"rgba(0,0,0,0.75)"}}>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 22 }}
            className="dribbble-panel max-w-md w-full max-h-[90vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E2D4] shrink-0 bg-white rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white shadow-md shadow-cyan-300/40">
                  <Calendar className="w-4 h-4" />
                </div>
                <h2 className="text-lg font-bold text-[#2B2B2B]">
                  {editingAppointment ? 'Randevuyu Düzenle' : 'Yeni Randevu'}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingAppointment(null); }}
                className="p-1.5 text-[#B0A99A] hover:text-[#8A8676] hover:bg-[#F0EBE1] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitForm} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* Hasta seçimi */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-[#2B2B2B]">
                    Hasta <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="pet_id"
                    value={formData.pet_id}
                    onChange={handleFormChange}
                    className={`w-full rounded-xl border-2 px-3 py-2.5 text-sm bg-white outline-none focus:ring-4 transition-all ${
                      !formData.pet_id
                        ? 'border-[#E8E2D4] focus:border-teal-400 focus:ring-teal-500/20'
                        : 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/20 bg-emerald-50/30'
                    }`}
                    required
                  >
                    <option value="">Hasta seçiniz...</option>
                    {pets.map(pet => (
                      <option key={pet.id} value={pet.id}>
                        {pet.name} {pet.owner?.name ? `· ${pet.owner.name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Randevu türü - kart seçici */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-[#2B2B2B]">
                    Randevu Türü <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { val: 'checkup', label: 'Kontrol', icon: '🩺' },
                      { val: 'vaccination', label: 'Aşı', icon: '💉' },
                      { val: 'surgery', label: 'Ameliyat', icon: '🏥' },
                      { val: 'emergency', label: 'Acil', icon: '🚨' },
                      { val: 'other', label: 'Diğer', icon: '📋' },
                    ].map((t) => (
                      <button
                        key={t.val}
                        type="button"
                        onClick={() => setFormData({ ...formData, type: t.val as any })}
                        className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border-2 transition-all ${
                          formData.type === t.val
                            ? 'border-cyan-500 bg-cyan-50 shadow-md shadow-cyan-200/40'
                            : 'border-[#E8E2D4] bg-white hover:border-[#D2DEB4] hover:bg-cyan-50/30'
                        }`}
                      >
                        <span className="text-xl">{t.icon}</span>
                        <span className={`text-[10px] font-semibold ${formData.type === t.val ? 'text-cyan-700' : 'text-[#8A8676]'}`}>
                          {t.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tarih + Saat */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-semibold text-[#2B2B2B]">
                      Tarih <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      min={editingAppointment ? undefined : todayLocal()}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border-2 border-[#E8E2D4] focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white"
                      required
                    />
                    {formData.date && formData.date < todayLocal() && (
                      <p className="text-xs text-amber-600">Uyarı: Geçmiş bir tarih seçtiniz.</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-semibold text-[#2B2B2B]">
                      Saat <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border-2 border-[#E8E2D4] focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white"
                      required
                    />
                  </div>
                </div>

                {/* Notlar */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-[#2B2B2B]">
                    Notlar <span className="text-[#B0A99A] font-normal">(Opsiyonel)</span>
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={3}
                    className="w-full rounded-xl border-2 border-[#E8E2D4] focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white resize-none"
                    placeholder="Randevu hakkında not..."
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 p-4 border-t border-[#E8E2D4] bg-white shrink-0 rounded-b-2xl">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingAppointment(null); }}
                  className="btn btn-secondary flex-1"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={!formData.pet_id || !formData.date || !formData.time}
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {editingAppointment ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}

      <DeleteConfirmModal
        isOpen={!!appointmentToDelete}
        onClose={() => setAppointmentToDelete(null)}
        onConfirm={() => appointmentToDelete ? handleDelete(appointmentToDelete.id) : undefined}
        title="Randevuyu Sil"
        message="Bu randevu kalıcı olarak silinecek. Bu işlem geri alınamaz."
        itemName={appointmentToDelete
          ? `${pets.find(p => p.id === (appointmentToDelete.pet_id || appointmentToDelete.petId))?.name || 'Hasta'} · ${appointmentToDelete.date} ${appointmentToDelete.time || ''}`.trim()
          : undefined}
      />
    </div>
  );
};

export default Appointments; 